<script>
// Renders a Font Awesome icon as an inline <svg> straight from the Vue render,
// using the icon definitions already loaded by the FA SVG-with-JS kit
// (window.FontAwesome). A plain <i class="fa-..."> is instead swapped for an
// <svg> by FA's MutationObserver one node at a time after mount, which stalls
// big lists (e.g. 1000-row tables) and leaves Vue holding a detached <i>.
//
// Usage: <app-icon icon="fa-solid fa-check" class="text-success" />
// `icon` takes the family + icon classes only; put any other class on `class`.

// abstract tree per icon string; null = unknown to FA (falls back to <i>).
const abstractCache = {}

const resolveAbstract = (icon) => {
  if (Object.prototype.hasOwnProperty.call(abstractCache, icon)) {
    return abstractCache[icon]
  }

  const fa = window.FontAwesome
  // FA scripts load with `defer`, so they may boot after the first render.
  // Don't cache that miss — the next render resolves it.
  if (!fa || typeof fa.icon !== 'function' || !fa.parse) {
    return null
  }

  const rendered = fa.icon(fa.parse.icon(icon))
  abstractCache[icon] = rendered ? rendered.abstract[0] : null

  return abstractCache[icon]
}

const toVNode = (h, node) => {
  const { class: staticClass, ...attrs } = node.attributes || {}

  return h(node.tag, { staticClass, attrs }, (node.children || []).map((child) => toVNode(h, child)))
}

export default {
  name: 'AppIcon',
  functional: true,
  props: {
    icon: { type: String, required: true },
  },
  render(h, { props, data }) {
    const abstract = resolveAbstract(props.icon)

    if (!abstract) {
      // Unknown icon / FA not booted yet: let FA convert it the legacy way.
      return h('i', { ...data, staticClass: [props.icon, data.staticClass].filter(Boolean).join(' ') })
    }

    const { class: svgClass, ...svgAttrs } = abstract.attributes

    return h(
      'svg',
      {
        ...data,
        staticClass: [svgClass, data.staticClass].filter(Boolean).join(' '),
        attrs: { ...svgAttrs, ...data.attrs },
      },
      (abstract.children || []).map((child) => toVNode(h, child)),
    )
  },
}
</script>
